import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import {
  QuestionEditorComponent,
  QuestionEditorValue,
} from '../question-editor/question-editor.component';
import { toCreateDto, toEditorValue } from '../question-editor/question-editor.mapper';

import { QuestionBank, QuestionSource } from '../../../../core/interfaces/question';
import { QuestionSourceComponent } from '../question-source/question-source.component';
import { QuestionService } from '../../../../core/services/question.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { MathTextComponent } from '../../../../shared/math/math-text.component';
import { MathPlainPipe } from '../../../../shared/math/math-plain.pipe';

/**
 * Édition d'une question existante, sur la même page — et le même éditeur — que
 * la création.
 *
 * <p>La portée (niveau, matière, chapitre) n'est pas redemandée : une question
 * appartient déjà à un chapitre, la déplacer n'est pas le propos de cet écran.
 * On charge la question avec ses réponses, on la traduit en valeur d'éditeur
 * ({@link toEditorValue}), puis on renvoie la saisie via `PUT`.</p>
 *
 * <p>Rappel du domaine, non géré ici mais assumé par le serveur : modifier une
 * question déjà passée par un élève n'écrase pas l'originale — une nouvelle
 * version est créée pour préserver les copies. Une question archivée est
 * immuable : la banque n'en propose pas l'édition.</p>
 */
@Component({
  selector: 'app-question-edit',
  standalone: true,
  imports: [MathTextComponent, MathPlainPipe, TranslateModule, ErrorStateComponent, QuestionEditorComponent, QuestionSourceComponent],
  templateUrl: './question-edit.component.html',
  styleUrl: './question-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionEditComponent implements OnInit {
  private readonly questions = inject(QuestionService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);

  readonly loading = signal(false);
  readonly errored = signal(false);
  readonly saving = signal(false);

  /** Valeur d'amorce transmise à l'éditeur une fois la question chargée. */
  readonly initial = signal<QuestionEditorValue | null>(null);

  /** Titre affiché en en-tête, tiré de la question chargée. */
  readonly heading = signal('');

  private questionId: number | null = null;
  /** Chapitre d'origine, reconduit tel quel à l'enregistrement. */
  private themeId: number | null = null;
  /**
   * Écran d'où l'on vient (la banque, avec ses filtres). Absent en accès
   * direct : on retombe alors sur la page précédente.
   */
  private returnUrl: string | null = null;

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    const raw = this.route.snapshot.paramMap.get('id');
    const id = Number(raw);
    if (!id) {
      this.errored.set(true);
      return;
    }
    this.questionId = id;
    this.loadQuestion(id);
  }

  loadQuestion(id: number): void {
    this.loading.set(true);
    this.errored.set(false);

    this.questions.getQuestionWithAnswers(id).subscribe({
      next: (res) => {
        const question = res?.status === 'SUCCESS' ? res.data : null;
        if (!question) {
          this.fail();
          return;
        }
        this.applyQuestion(question);
        this.loading.set(false);
      },
      error: (_: HttpErrorResponse) => this.fail(),
    });
  }

  onSave(value: QuestionEditorValue): void {
    if (this.questionId === null || this.themeId === null || this.saving()) {
      return;
    }
    this.saving.set(true);

    this.questions
      .updateQuestion(this.questionId, toCreateDto(value, this.themeId))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.swal.success(this.translate.instant('QUESTION_EDIT.SAVE_SUCCESS'));
          this.leave();
        },
        error: () => {
          // L'intercepteur global a déjà signalé l'échec ; la saisie reste
          // intacte pour ne rien perdre.
          this.saving.set(false);
        },
      });
  }

  onCancel(): void {
    this.leave();
  }

  retry(): void {
    if (this.questionId !== null) this.loadQuestion(this.questionId);
  }

  /**
   * D'où vient la question, quand elle a été importée.
   *
   * <p>Séparée de la valeur éditable : la provenance se lit, elle ne se
   * modifie pas. Elle reste vide pour une question saisie à la main.</p>
   */
  readonly source = signal<QuestionSource | null>(null);

  private applyQuestion(question: QuestionBank): void {
    this.themeId = question.themeId ?? null;
    this.heading.set(question.title ?? '');
    this.initial.set(toEditorValue(question));

    // La provenance ne passe pas par l'éditeur : elle ne se modifie pas, elle
    // se lit. La mêler à la valeur éditable laisserait croire l'inverse.
    this.source.set(question.source ?? null);
  }

  private fail(): void {
    this.initial.set(null);
    this.errored.set(true);
    this.loading.set(false);
  }

  private leave(): void {
    if (this.returnUrl) this.router.navigateByUrl(this.returnUrl);
    else this.location.back();
  }
}
